import { useCallback, useMemo, useState } from 'react';
import type { Card } from '../../../shared/types';

/**
 * A run through a section's deck, cut into sittings.
 *
 * A 186-card section is not one sitting, and the old behaviour made that
 * awkward: a 20-card session always took the *first* 20 of a fresh shuffle, so
 * finishing one and starting another re-drew from the whole deck and you could
 * see the same twenty again while never meeting the other hundred and sixty.
 *
 * A run fixes the order once and keeps a cursor into it. Each sitting is the
 * next slice, so working through a big section in chunks actually covers it.
 * The order is fixed at the start rather than per sitting for the same reason a
 * dealer does not reshuffle mid-hand — "next twenty" has to mean twenty you
 * have not seen.
 */

function shuffled<T>(items: T[]): T[] {
  // Fisher-Yates. Array.sort with a random comparator is biased and, in some
  // engines, can throw on an inconsistent comparator.
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export interface DeckRun {
  /** The cards for the sitting now in progress. */
  sitting: Card[];
  /** 1-based index of this sitting within the run. */
  sittingNumber: number;
  /** How many sittings the whole run takes at the current size. */
  sittingCount: number;
  /** Cards before this sitting — already studied in this run. */
  studiedBefore: number;
  /** Cards after this sitting, still unseen in this run. */
  remainingAfter: number;
  /** True when this sitting is the whole deck. */
  isWholeDeck: boolean;
  /** Advances to the next slice. Returns the cards, or null if the run is over. */
  advance: () => Card[] | null;
  /** Re-orders the deck and starts the run again from the top. */
  restartRun: () => Card[];
}

export function useDeckRun(deck: Card[], shuffle: boolean, sittingSize: number): DeckRun {
  // Fixed once, at mount. Changing the card setup mid-run must not reorder the
  // cards underneath a cursor that is pointing into the old order.
  const [order, setOrder] = useState<Card[]>(() => (shuffle ? shuffled(deck) : [...deck]));
  const [cursor, setCursor] = useState(0);

  // A size of 0, or one at least as large as the deck, means a single sitting.
  const size = sittingSize > 0 && sittingSize < order.length ? sittingSize : order.length;

  const sitting = useMemo(() => order.slice(cursor, cursor + size), [order, cursor, size]);

  const advance = useCallback((): Card[] | null => {
    const next = cursor + size;
    if (next >= order.length) return null;
    setCursor(next);
    return order.slice(next, next + size);
  }, [cursor, size, order]);

  const restartRun = useCallback((): Card[] => {
    const reordered = shuffle ? shuffled(deck) : [...deck];
    setOrder(reordered);
    setCursor(0);
    const nextSize = sittingSize > 0 && sittingSize < reordered.length ? sittingSize : reordered.length;
    return reordered.slice(0, nextSize);
  }, [deck, shuffle, sittingSize]);

  return {
    sitting,
    sittingNumber: size > 0 ? Math.floor(cursor / size) + 1 : 1,
    sittingCount: size > 0 ? Math.ceil(order.length / size) : 1,
    studiedBefore: cursor,
    remainingAfter: Math.max(0, order.length - (cursor + sitting.length)),
    isWholeDeck: sitting.length === order.length,
    advance,
    restartRun,
  };
}
